/**
 * Per-capita denominator wiring (E2.3) for PaCC surfaces.
 *
 * The pure matching logic (iso3 → admin-name → nearest-centroid) lives in
 * @biomax/plugin-surveillance-feed (resolveDenominator / perCapita100k) so it
 * stays offline-testable and the feed package keeps zero dependency on the
 * region catalog. Here we just project the live region catalog into the
 * minimal PopulationDenominator table that helper consumes, once per session.
 */

import { listRegions } from '@biomax/plugin-region-database';
import {
  resolveDenominator, perCapita100k,
  type PopulationDenominator, type ResolvedDenominator, type Signal,
} from '@biomax/plugin-surveillance-feed';

/** Build the denominator table from the live region catalog (memoised). */
let DENOMS: PopulationDenominator[] | null = null;
export function populationDenominators(): PopulationDenominator[] {
  if (DENOMS) return DENOMS;
  DENOMS = listRegions()
    .filter(r => r.population?.total > 0)
    .map(r => ({
      ...(r.names.iso3 ? { iso3: r.names.iso3 } : {}),
      name: r.names.english,
      population: r.population.total,
      ...(r.geometry?.centroidLatLon ? { centroidLatLon: r.geometry.centroidLatLon } : {}),
    }));
  return DENOMS;
}

/** Resolve a signal to a population denominator using the live region catalog. */
export function denominatorFor(signal: Pick<Signal, 'location'>): ResolvedDenominator | null {
  return resolveDenominator(signal, populationDenominators());
}

/**
 * Per-100k rate for a signal's value, or null when no denominator can be
 * matched confidently. Callers MUST fall back to the absolute count + a
 * "no denominator" note rather than showing a wrong/zero rate.
 */
export function perCapitaFor(signal: Pick<Signal, 'location' | 'value'>): {
  rate: number; matchedBy: ResolvedDenominator['matchedBy']; population: number;
} | null {
  const d = denominatorFor(signal);
  if (!d) return null;
  const rate = perCapita100k(signal.value, d.population);
  if (rate == null) return null;
  return { rate, matchedBy: d.matchedBy, population: d.population };
}

/** Display string for a per-100k rate (≥2 sig figs for small rates). */
export function fmtPer100k(rate: number): string {
  if (rate >= 100) return rate.toLocaleString(undefined, { maximumFractionDigits: 0 });
  if (rate >= 1) return rate.toLocaleString(undefined, { maximumFractionDigits: 1 });
  return rate.toLocaleString(undefined, { maximumFractionDigits: 2 });
}
